import React from 'react';
import { Input } from '@/components/ui/Input/Input';
import { __resolve } from '@/lib/i18n';
import type { ThemeFormValues } from '../ThemeEditorPanel';
import { SectionWrapper } from '../SectionWrapper';

const CURATED_FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Poppins', 'Nunito',
  'Montserrat', 'Raleway', 'Source Sans 3', 'Work Sans', 'DM Sans',
  'Plus Jakarta Sans', 'IBM Plex Sans', 'Outfit', 'Space Grotesk',
];

const HEADING_FONT_SIZE_OPTIONS = [
  { value: '', label: __resolve('ui::Default (32px)') },
  { value: '1.5rem', label: __resolve('ui::24px') },
  { value: '1.75rem', label: __resolve('ui::28px') },
  { value: '2rem', label: __resolve('ui::32px') },
  { value: '2.25rem', label: __resolve('ui::36px') },
  { value: '2.5rem', label: __resolve('ui::40px') },
];

const BODY_FONT_SIZE_OPTIONS = [
  { value: '', label: __resolve('ui::Default (16px)') },
  { value: '0.75rem', label: __resolve('ui::12px') },
  { value: '0.875rem', label: __resolve('ui::14px') },
  { value: '1rem', label: __resolve('ui::16px') },
  { value: '1.125rem', label: __resolve('ui::18px') },
  { value: '1.25rem', label: __resolve('ui::20px') },
];

const DATE_FORMAT_OPTIONS = [
  { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
];

const TIME_FORMAT_OPTIONS = [
  { value: 'HH:mm', label: __resolve('ui::HH:mm (24h)') },
  { value: 'hh:mm A', label: __resolve('ui::hh:mm A (12h)') },
  { value: 'HH:mm:ss', label: 'HH:mm:ss' },
];

const WEEK_START_OPTIONS = [
  { value: 1, label: __resolve('ui::Monday') },
  { value: 0, label: __resolve('ui::Sunday') },
];

const THOUSANDS_SEPARATOR_OPTIONS = [
  { value: '.', label: __resolve('ui::. (period)') },
  { value: ',', label: __resolve('ui::, (comma)') },
  { value: "'", label: __resolve("ui::' (apostrophe)") },
  { value: ' ', label: __resolve('ui::(space)') },
];

const DECIMAL_SEPARATOR_OPTIONS = [
  { value: ',', label: __resolve('ui::, (comma)') },
  { value: '.', label: __resolve('ui::. (period)') },
];

const CURRENCY_FORMAT_OPTIONS = [
  { value: 'value_first', label: __resolve('ui::XX,YY € (value first)') },
  { value: 'symbol_first', label: __resolve('ui::€ XX.YY (symbol first)') },
];

// Default values for locale settings
const LOCALE_DEFAULTS = {
  dateFormat: 'DD.MM.YYYY',
  timeFormat: 'HH:mm',
  weekStart: 1,
  thousandsSeparator: '.',
  decimalSeparator: ',',
  currencySymbol: '\u20ac',
  currencyFormat: 'value_first',
};

function defaultLabel(options: { value: any; label: string }[], defaultValue: any): string {
  const match = options.find(o => String(o.value) === String(defaultValue));
  return match ? __resolve('ui::Default ({{label}})', { label: match.label }) : __resolve('ui::Default');
}

const BORDER_RADIUS_OPTIONS = [
  { value: '', label: __resolve('ui::Default (Medium)') },
  { value: 'none', label: __resolve('ui::None') },
  { value: 'sm', label: __resolve('ui::Small') },
  { value: 'md', label: __resolve('ui::Medium') },
  { value: 'lg', label: __resolve('ui::Large') },
  { value: 'xl', label: 'XL' },
];

interface ThemeSettingsSectionProps {
  form: ThemeFormValues;
  onChange: <K extends keyof ThemeFormValues>(key: K, value: ThemeFormValues[K]) => void;
  platformDefaults?: Partial<ThemeFormValues> | null;
}

const selectClass = 'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm';

export function ThemeSettingsSection({ form, onChange, platformDefaults }: ThemeSettingsSectionProps) {
  return (
    <SectionWrapper title="Theme Settings">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* ── Typography ── */}
        <div className="p-5 rounded-lg border border-border bg-background">
          <h3 className="text-lg font-semibold mb-4">Typography</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Heading Font</label>
              <select
                className={selectClass}
                value={form.headingFontFamily}
                onChange={(e) => onChange('headingFontFamily', e.target.value)}
              >
                <option value="">{platformDefaults?.headingFontFamily ? __resolve('ui::Inherited ({{font}})', { font: platformDefaults.headingFontFamily }) : __resolve('ui::Default (Inter)')}</option>
                {CURATED_FONTS.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Body Font</label>
              <select
                className={selectClass}
                value={form.bodyFontFamily}
                onChange={(e) => onChange('bodyFontFamily', e.target.value)}
              >
                <option value="">{platformDefaults?.bodyFontFamily ? __resolve('ui::Inherited ({{font}})', { font: platformDefaults.bodyFontFamily }) : __resolve('ui::Default (Inter)')}</option>
                {CURATED_FONTS.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Heading Size</label>
                <select
                  className={selectClass}
                  value={form.headingFontSize}
                  onChange={(e) => onChange('headingFontSize', e.target.value)}
                >
                  {HEADING_FONT_SIZE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Body Size</label>
                <select
                  className={selectClass}
                  value={form.bodyFontSize}
                  onChange={(e) => onChange('bodyFontSize', e.target.value)}
                >
                  {BODY_FONT_SIZE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* ── Border Radius ── */}
        <div className="p-5 rounded-lg border border-border bg-background">
          <h3 className="text-lg font-semibold mb-4">Border Radius</h3>
          <div className="flex flex-wrap gap-2">
            {BORDER_RADIUS_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange('borderRadius', opt.value)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  form.borderRadius === opt.value
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'bg-background text-foreground border-border hover:border-primary-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <h3 className="text-lg font-semibold mt-6 mb-4">Display</h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.darkModeEnabled}
              onChange={(e) => onChange('darkModeEnabled', e.target.checked)}
              className="rounded border-border"
            />
            <span className="text-sm text-foreground">Allow dark mode</span>
          </label>
        </div>

        {/* ── Branding Assets ── */}
        <div className="p-5 rounded-lg border border-border bg-background">
          <h3 className="text-lg font-semibold mb-4">Branding</h3>
          <div className="space-y-3">
            <Input
              label="Logo URL"
              type="url"
              value={form.logoUrl}
              onChange={(e) => onChange('logoUrl', e.target.value)}
              placeholder={platformDefaults?.logoUrl || 'https://example.com/logo.png'}
            />
            <Input
              label="Logo Mark URL"
              type="url"
              value={form.logoMarkUrl}
              onChange={(e) => onChange('logoMarkUrl', e.target.value)}
              placeholder={platformDefaults?.logoMarkUrl || 'https://example.com/mark.png'}
            />
            <Input
              label="Favicon URL"
              type="url"
              value={form.faviconUrl}
              onChange={(e) => onChange('faviconUrl', e.target.value)}
              placeholder={platformDefaults?.faviconUrl || 'https://example.com/favicon.ico'}
            />
          </div>
        </div>

        {/* ── Locale & Formatting ── */}
        <div className="p-5 rounded-lg border border-border bg-background">
          <h3 className="text-lg font-semibold mb-4">Locale & Formatting</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Date Format</label>
                <select
                  className={selectClass}
                  value={form.dateFormat}
                  onChange={(e) => onChange('dateFormat', e.target.value)}
                >
                  <option value="">{defaultLabel(DATE_FORMAT_OPTIONS, LOCALE_DEFAULTS.dateFormat)}</option>
                  {DATE_FORMAT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Time Format</label>
                <select
                  className={selectClass}
                  value={form.timeFormat}
                  onChange={(e) => onChange('timeFormat', e.target.value)}
                >
                  <option value="">{defaultLabel(TIME_FORMAT_OPTIONS, LOCALE_DEFAULTS.timeFormat)}</option>
                  {TIME_FORMAT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Week Start</label>
                <select
                  className={selectClass}
                  value={form.weekStart}
                  onChange={(e) => {
                    const v = e.target.value;
                    onChange('weekStart', v === '' ? '' : Number(v));
                  }}
                >
                  <option value="">{defaultLabel(WEEK_START_OPTIONS, LOCALE_DEFAULTS.weekStart)}</option>
                  {WEEK_START_OPTIONS.map(opt => (
                    <option key={String(opt.value)} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.showCalendarWeeks}
                    onChange={(e) => onChange('showCalendarWeeks', e.target.checked)}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-foreground">Show calendar weeks</span>
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Thousands Separator</label>
                <select
                  className={selectClass}
                  value={form.thousandsSeparator}
                  onChange={(e) => onChange('thousandsSeparator', e.target.value)}
                >
                  <option value="">{defaultLabel(THOUSANDS_SEPARATOR_OPTIONS, LOCALE_DEFAULTS.thousandsSeparator)}</option>
                  {THOUSANDS_SEPARATOR_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Decimal Separator</label>
                <select
                  className={selectClass}
                  value={form.decimalSeparator}
                  onChange={(e) => onChange('decimalSeparator', e.target.value)}
                >
                  <option value="">{defaultLabel(DECIMAL_SEPARATOR_OPTIONS, LOCALE_DEFAULTS.decimalSeparator)}</option>
                  {DECIMAL_SEPARATOR_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Currency Symbol</label>
                <input
                  type="text"
                  className={selectClass}
                  value={form.currencySymbol}
                  onChange={(e) => onChange('currencySymbol', e.target.value)}
                  placeholder={LOCALE_DEFAULTS.currencySymbol}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Currency Format</label>
                <select
                  className={selectClass}
                  value={form.currencyFormat}
                  onChange={(e) => onChange('currencyFormat', e.target.value)}
                >
                  <option value="">{defaultLabel(CURRENCY_FORMAT_OPTIONS, LOCALE_DEFAULTS.currencyFormat)}</option>
                  {CURRENCY_FORMAT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}
